// Maps a backend error code to a message key. Returns null when the caller should
// fall back to its own per-operation copy — which is the important half: on the
// English path the FE never renders server prose, because the ~23 unmapped backend
// messages are Finnish permission guards, not user-actionable text.
//
// RATE_LIMITED is shared by two different backend messages (checkAddress vs the
// generic preview/login attempts limiter) — the code alone can't tell them apart,
// so it maps to the generic one. errors.addressCheckRateLimited exists for a future
// call site that has enough context to know it's the address-check flow specifically
// (no current call site does — RequestForm's checkAddress 429 is swallowed silently).
const CODE_TO_KEY = {
    RATE_LIMITED: "errors.tooManyAttempts",
    REQUEST_ALREADY_SENT: "errors.requestAlreadySent",
    REQUEST_NOT_EDITABLE: "errors.requestNotEditable",
    REQUEST_HAS_OFFERS: "errors.requestHasOffers",
    REQUEST_NOT_FOUND: "errors.requestNotFound",
    NO_ACCOUNT_ACCESS: "errors.noAccountAccess",
    // Email-code login (fb#2184)
    ACCOUNT_NOT_FOUND: "errors.accountNotFound",
    LOGIN_SESSION_INVALID: "errors.loginSessionInvalid",
    LOGIN_CODE_INVALID: "errors.loginCodeInvalid",
};

export function errorMessageKey(err) {
    return CODE_TO_KEY[err?.code] || null;
}

/**
 * Resolves the message to show for a thrown API error, preserving the Finnish
 * invariant: on lang === "fi" the server's own prose always wins — exactly what
 * the UI showed before any of this existed — regardless of what the FE bundles
 * say for a mapped code. Bundle text (mapped or fallback) is only ever rendered
 * on the English path, since English must never show raw Finnish server prose.
 *
 *   lang === "fi" → err.message, else t(mapped key) if code maps, else t(fallbackKey)
 *   otherwise     → t(mapped key) if code maps, else t(fallbackKey)
 *
 * @param {{message?: string, code?: string}} err
 * @param {{t: (key: string, vars?: object) => string, lang: string, fallbackKey: string}} opts
 */
export function resolveErrorMessage(err, { t, lang, fallbackKey }) {
    // Errors WE threw were already built with t() in the caller's language, so
    // their message is the most specific copy available in either language —
    // trust it. Only errors carrying this flag qualify; the English path must
    // keep discarding unflagged messages, because those are Finnish server prose.
    // Without this, an English user who picks a 30 MB image is told "Upload
    // failed" instead of "the image is too large", and the actionable half of
    // the message is lost to the generic fallback.
    if (err?.localized && err.message) return err.message;
    const key = errorMessageKey(err);
    if (lang === "fi") return err?.message || (key ? t(key) : t(fallbackKey));
    return key ? t(key) : t(fallbackKey);
}
